import { usePage } from '@inertiajs/react';
import { useCallback, useMemo } from 'react';


/**
 * Laravel-based i18n hook. Reads translations shared via Inertia middleware.
 * Usage: const { t, locale } = useTranslations();
 *        t('common.save') => 'Save'
 *        t('dashboard.welcome_back', { name: 'John' }) => 'Welcome back, John'
 */
type TranslationNode = string | { [key: string]: TranslationNode };

export function useTranslations() {
    const props = usePage<{
        locale: string;
        translations: Record<string, TranslationNode>;
        fallbackTranslations: Record<string, TranslationNode>;
    }>().props;
    const locale = props.locale ?? 'en';
    const translations = props.translations ?? {};
    const fallbackTranslations = props.fallbackTranslations ?? {};

    // `t` must keep a stable identity between renders: consumers list it in
    // effect dependency arrays, and a fresh closure each render would re-fire
    // those effects on every render (potentially looping into React error
    // #185). It only needs to change when the underlying dictionaries do.
    const t = useCallback(
        (key: string, replacements?: Record<string, string>): string => {
            const resolve = (source: Record<string, TranslationNode>, segments: string[]): string | undefined => {
                let cursor: TranslationNode | undefined = source[segments[0]];
                for (let i = 1; i < segments.length; i++) {
                    if (cursor === undefined || typeof cursor === 'string') return undefined;
                    cursor = cursor[segments[i]];
                }
                return typeof cursor === 'string' ? cursor : undefined;
            };

            const segments = key.split('.');

            let value = resolve(translations, segments)
                ?? resolve(fallbackTranslations, segments)
                ?? key;

            // Pick a plural form before placeholder substitution. Mirrors Laravel's
            // `singular|plural` syntax (we only support 2 forms, not the full
            // `{0} ...|[1,*] ...` ranges). Triggered by a `count` replacement.
            if (value.includes('|') && replacements && 'count' in replacements) {
                const [singular, plural] = value.split('|', 2);
                value = replacements.count === '1' ? singular : (plural ?? singular);
            }

            if (replacements) {
                Object.entries(replacements).forEach(([placeholder, replacement]) => {
                    value = value.replace(`:${placeholder}`, replacement);
                });
            }

            return value;
        },
        [translations, fallbackTranslations],
    );

    return useMemo(() => ({ t, locale }), [t, locale]);
}
